import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categoryArticles, categoryReviews } from '@/mocks/categories';
import { categoryCards } from '@/mocks/homepage';
import { WebPageSchema, BreadcrumbListSchema } from '@/components/base/SchemaOrg';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const catData = slug ? categoryArticles[slug as keyof typeof categoryArticles] : null;

  if (!catData) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-background-50 pt-20">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900">分类不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { name: '首页', url: 'http://cgbast.cn/' },
    { name: catData.title, url: 'http://cgbast.cn/' },
  ];

  return (
    <>
      <WebPageSchema
        title={`${catData.title} - 每日大赛黑料网`}
        description={catData.description}
        url={'http://cgbast.cn/'}
      />
      <BreadcrumbListSchema items={breadcrumbItems} />
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <div className="pt-24 pb-0 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 py-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium">{catData.title}</span>
            </nav>
          </div>
        </div>

        {/* Category Grid Section - sub categories */}
        <section className="py-12 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 md:mb-14">
              <div>
                <span className="text-foreground-400 text-xs tracking-widest uppercase font-medium font-heading">
                  子分类
                </span>
                <h2 className="text-3xl md:text-5xl font-black text-foreground-900 font-heading leading-tight mt-2">
                  {catData.title}<span className="italic font-light text-foreground-400">专区</span>
                </h2>
              </div>
              <p className="text-foreground-500 text-sm max-w-md leading-relaxed">
                {catData.description}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {categoryCards.map((cat) => {
                const isActive = cat.slug === slug;
                return (
                  <Link
                    key={cat.id}
                    to="/"
                    className={`group rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02] ${
                      isActive ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-background-100' : ''
                    }`}
                  >
                    <div className={`aspect-[3/4] relative ${cat.color}`}>
                      <img
                        src={cat.imageUrl}
                        alt={cat.title}
                        title={cat.title}
                        className="w-full h-[85%] object-cover object-top"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-background-50 px-4 py-3.5">
                        <h3 className="text-foreground-900 font-bold text-sm md:text-base font-heading">
                          {cat.title}
                        </h3>
                        <p className="text-foreground-500 text-xs mt-0.5">{cat.description}</p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-12 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10">
            <h2 className="text-2xl md:text-3xl font-black text-foreground-900 font-heading mb-10">
              最新<span className="text-primary-500">{catData.title}</span>爆料
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {catData.articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-2xl overflow-hidden cursor-pointer border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="aspect-[3/2] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-primary-600 text-xs font-medium bg-primary-50 px-2.5 py-1 rounded-full">
                      {catData.title}
                    </span>
                    <h3 className="text-foreground-900 font-bold text-sm md:text-base mt-3 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-foreground-500 text-xs mt-2 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-background-200/70">
                      <span className="text-foreground-400 text-xs">{article.date}</span>
                      <span className="text-foreground-400 text-xs flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-12 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 text-accent-600 text-sm font-medium mb-3">
                <i className="ri-chat-heart-line"></i>
                <span>用户口碑</span>
              </div>
              <h2 className="text-2xl md:text-4xl font-black text-foreground-900 font-heading">
                吃瓜群众<span className="text-accent-500">怎么说</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {categoryReviews.slice(0, 5).map((review, idx) => {
                if (review.isImageCard) {
                  return (
                    <div
                      key={review.id}
                      className="relative rounded-2xl overflow-hidden aspect-[3/4]"
                    >
                      <img
                        src={review.imageUrl}
                        alt={review.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                        <p className="text-background-50 font-medium text-sm">{review.name}</p>
                        <p className="text-background-50/70 text-xs">{review.role}</p>
                      </div>
                    </div>
                  );
                }

                if (idx === 3) {
                  return (
                    <div
                      key={review.id}
                      className="relative rounded-2xl overflow-hidden aspect-[3/2] md:col-span-2"
                    >
                      <img
                        src="https://readdy.ai/api/search-image?query=Group%20of%20young%20Chinese%20people%20discussing%20and%20laughing%20around%20a%20phone%2C%20warm%20cafe%20atmosphere%2C%20golden%20hour%20lighting%2C%20lifestyle%20documentary%20photography%2C%20candid%20moment&width=1000&height=600&seq=cat-testimonial-wide&orientation=landscape"
                        alt="吃瓜社区"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                        <p className="text-background-50 font-semibold">{review.name}</p>
                        <p className="text-background-50/70 text-xs">{review.role}</p>
                        <p className="text-background-50/80 text-sm mt-2 leading-relaxed">{review.text}</p>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={review.id}
                    className="bg-secondary-50 rounded-2xl p-5 md:p-6"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-foreground-900 font-semibold text-sm">{review.name}</p>
                        <p className="text-foreground-500 text-xs">{review.role}</p>
                      </div>
                    </div>
                    <i className="ri-double-quotes-l text-secondary-300 text-2xl block mb-2"></i>
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      {review.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}